import React, { useEffect, useState } from 'react';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar, Clock, CheckCircle, Package, BookOpen, AlertCircle, Trash2 } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BOOKSHELF_TYPES = [
  "Infant/Toddler Bookshelf",
  "Standard Size Bookshelf"
];

const BOOK_TYPES = [
  "Board Books",
  "Elementary Age Books",
  "Teen Books"
];

export default function PendingSchedules() {
  const [schedules, setSchedules] = useState([]);
  const [families, setFamilies] = useState({});
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, schedule: null, selectedSlot: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, scheduleId: null });
  const [clearAllDialog, setClearAllDialog] = useState(false);
  const [deliveryItems, setDeliveryItems] = useState({
    bookshelves: {},
    books: {}
  });
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, familiesRes, stagedFamiliesRes, inventoryRes] = await Promise.all([
        api.get('/delivery-schedules'),
        api.get('/families'),
        api.get('/families/staged'),
        api.get('/inventory')
      ]);
      
      // Filter only pending schedules
      const pendingSchedules = schedulesRes.data.filter(s => s.status === 'pending');
      setSchedules(pendingSchedules);
      
      // Create family lookup map (include both main list and staged families)
      const familyMap = {};
      familiesRes.data.forEach(f => {
        familyMap[f.id] = f;
      });
      stagedFamiliesRes.data.forEach(f => {
        familyMap[f.id] = f;
      });
      setFamilies(familyMap);
      
      setInventory(inventoryRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const getInventoryQuantity = (category, itemType) => {
    const item = inventory.find(i => i.category === category && i.item_type === itemType);
    return item ? item.quantity : 0;
  };

  const openConfirmDialog = (schedule, slot) => {
    // Initialize delivery items with zeros
    const bookshelves = {};
    const books = {};
    
    BOOKSHELF_TYPES.forEach(type => {
      bookshelves[type] = 0;
    });
    
    BOOK_TYPES.forEach(type => {
      books[type] = 0;
    });
    
    setDeliveryItems({ bookshelves, books });
    setConfirmDialog({ open: true, schedule, selectedSlot: slot });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, schedule: null, selectedSlot: null });
    setDeliveryItems({ bookshelves: {}, books: {} });
  };

  const handleItemQuantityChange = (category, itemType, quantity) => {
    setDeliveryItems(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [itemType]: Math.max(0, parseInt(quantity) || 0)
      }
    }));
  };

  const handleConfirmSchedule = async () => {
    const { schedule, selectedSlot } = confirmDialog;
    
    // Build delivery items array
    const items = [];
    
    Object.entries(deliveryItems.bookshelves).forEach(([type, qty]) => {
      if (qty > 0) {
        items.push({ category: 'bookshelves', item_type: type, quantity: qty });
      }
    });
    
    Object.entries(deliveryItems.books).forEach(([type, qty]) => {
      if (qty > 0) {
        items.push({ category: 'books', item_type: type, quantity: qty });
      }
    });
    
    // Validate at least one item is selected
    if (items.length === 0) {
      toast.error('Please assign at least one item to this delivery');
      return;
    }
    
    setConfirming(true);
    try {
      await api.post(`/delivery-schedules/${schedule.id}/confirm`, {
        confirmed_slot: selectedSlot,
        delivery_items: items
      });
      
      toast.success('Delivery confirmed with items assigned!');
      closeConfirmDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm schedule');
    } finally {
      setConfirming(false);
    }
  };

  const handleDeleteSchedule = async () => {
    if (!deleteDialog.scheduleId) return;
    
    try {
      await api.delete(`/delivery-schedules/${deleteDialog.scheduleId}`);
      toast.success('Schedule deleted successfully');
      setDeleteDialog({ open: false, scheduleId: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete schedule');
    }
  };

  const handleClearAllPending = async () => {
    try {
      const response = await api.delete('/delivery-schedules/bulk/pending');
      toast.success(response.data.message);
      setClearAllDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear schedules');
    }
  };

  return (
    <div className="flex">
      <Sidebar />
      <div className="ml-64 flex-1 p-8" data-testid="pending-schedules-page">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">Pending Schedule Requests</h1>
              <p className="text-stone-600">Review and confirm delivery schedules with item assignments</p>
            </div>
            {schedules.length > 0 && (
              <Button
                variant="outline"
                onClick={() => setClearAllDialog(true)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="clear-all-pending-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All Pending
              </Button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : schedules.length === 0 ? (
            <Card className="border-stone-200 shadow-card">
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-16 h-16 text-green-300 mx-auto mb-4" />
                <p className="text-stone-600 mb-2">No pending schedule requests</p>
                <p className="text-sm text-stone-500">All schedule requests have been processed</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6" data-testid="pending-schedules-list">
              {schedules.map(schedule => {
                const family = families[schedule.family_id];
                // Use stored family info from schedule if family not found in main list
                const familyName = family 
                  ? `${family.parent_first_name} ${family.parent_last_name}` 
                  : schedule.family_name || 'Unknown Family';
                const familyAddress = family?.address || schedule.family_address;
                const familyEmail = family?.email || schedule.family_email;
                const familyPhone = family?.phone || schedule.family_phone;
                
                return (
                  <Card key={schedule.id} className="border-stone-200 shadow-card" data-testid={`schedule-${schedule.id}`}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-xl">
                            {familyName}
                          </CardTitle>
                          <div className="text-sm text-stone-600 mt-1">
                            {familyAddress && <p>{familyAddress}</p>}
                            {(familyEmail || familyPhone) && (
                              <p>{[familyEmail, familyPhone].filter(Boolean).join(' • ')}</p>
                            )}
                            {family?.ward_number && <p>Ward {family.ward_number}</p>}
                            {family?.city_county && <p>{family.city_county}</p>}
                            {family?.children && (
                              <p className="mt-1">{family.children?.length || 0} child{family.children?.length !== 1 ? 'ren' : ''}</p>
                            )}
                            {!family && !familyAddress && !familyEmail && (
                              <p className="text-amber-600 italic">
                                <AlertCircle className="w-3 h-3 inline mr-1" />
                                Family details not found - check New Families list
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-yellow-100 text-yellow-800">
                            <Clock className="w-3 h-3 mr-1" />
                            Pending Review
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteDialog({ open: true, scheduleId: schedule.id })}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-schedule-${schedule.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-4">
                        <p className="text-sm font-medium text-stone-700 mb-3">Preferred Time Slots:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {schedule.preferred_slots?.map((slot, idx) => (
                            <div
                              key={idx}
                              className="p-4 border-2 border-stone-200 rounded-lg hover:border-primary transition-colors"
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="font-semibold">
                                    {new Date(slot.date).toLocaleDateString('en-US', {
                                      weekday: 'short',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </p>
                                  <p className="text-sm text-stone-600">{slot.time_slot}</p>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => openConfirmDialog(schedule, slot)}
                                  className="rounded-full"
                                  data-testid={`confirm-slot-${schedule.id}-${idx}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Confirm
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      {schedule.notes && (
                        <div className="p-3 bg-stone-50 rounded-lg">
                          <p className="text-sm text-stone-600">
                            <strong>Notes:</strong> {schedule.notes}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Confirm Dialog with Item Assignment */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && closeConfirmDialog()}>
        <DialogContent className="max-w-2xl" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Delivery & Assign Items</DialogTitle>
            <DialogDescription>
              {confirmDialog.selectedSlot && (
                <span className="block mt-2 p-3 bg-primary/10 rounded-lg">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  {new Date(confirmDialog.selectedSlot.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })} at {confirmDialog.selectedSlot.time_slot}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Bookshelves Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-lg">Bookshelves</h3>
              </div>
              <div className="space-y-3">
                {BOOKSHELF_TYPES.map(type => {
                  const available = getInventoryQuantity('bookshelves', type);
                  return (
                    <div key={type} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{type}</p>
                        <p className="text-sm text-stone-500">Available: {available}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`bookshelf-${type}`} className="sr-only">Quantity</Label>
                        <Input
                          id={`bookshelf-${type}`}
                          type="number"
                          min="0"
                          max={available}
                          value={deliveryItems.bookshelves[type] || 0}
                          onChange={(e) => handleItemQuantityChange('bookshelves', type, e.target.value)}
                          className="w-20"
                          data-testid={`input-bookshelf-${type.replace(/\s+/g, '-').toLowerCase()}`}
                        />
                        {deliveryItems.bookshelves[type] > available && (
                          <AlertCircle className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Books Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-lg">Books</h3>
              </div>
              <div className="space-y-3">
                {BOOK_TYPES.map(type => {
                  const available = getInventoryQuantity('books', type);
                  return (
                    <div key={type} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{type}</p>
                        <p className="text-sm text-stone-500">Available: {available}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`book-${type}`} className="sr-only">Quantity</Label>
                        <Input
                          id={`book-${type}`}
                          type="number"
                          min="0"
                          max={available}
                          value={deliveryItems.books[type] || 0}
                          onChange={(e) => handleItemQuantityChange('books', type, e.target.value)}
                          className="w-20"
                          data-testid={`input-book-${type.replace(/\s+/g, '-').toLowerCase()}`}
                        />
                        {deliveryItems.books[type] > available && (
                          <AlertCircle className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeConfirmDialog} data-testid="cancel-confirm">
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSchedule} 
              disabled={confirming}
              className="rounded-full"
              data-testid="submit-confirm"
            >
              {confirming ? 'Confirming...' : 'Confirm Delivery'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Single Schedule Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, scheduleId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this pending schedule request. The family will need to submit a new request if they want to reschedule.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSchedule} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear All Pending Dialog */}
      <AlertDialog open={clearAllDialog} onOpenChange={setClearAllDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Pending Schedules?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete ALL pending schedule requests. This action cannot be undone. Families will need to submit new requests.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearAllPending} className="bg-red-600 hover:bg-red-700">
              Clear All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
